import {
	dateFormats,
	StringDateApiFormatType,
	type UserAMLQuestionnaireUserProvidedValues,
	type UserRequirement
} from '@capsicum/common';
import { atom, useAtom, useSetAtom } from 'jotai';
import { useEffect } from 'react';

import { uniqueRequirementAtom } from '../../data-access';
import { amlQuestionnaireReducerAtom } from '../../data-access/atoms/aml-questionnaire.atom';
import { useSubmitRequirementStateByIdentifierQuery } from '../../data-access/queries/useRequirementsQueries';

enum QuestionnaireSteps {
	AccountCreationReasons = 1,
	EstimatedAnnualIncome = 7,
	Pep = 8,
	PersonalInformation = 2,
	PostalAddress = 3,
	Profession = 4,
	SourcesOfIncome = 6,
	SpendingEstimatePerYear = 5,
	TermsAndConditions = 0
}

const stepIndexAtom = atom<QuestionnaireSteps>(QuestionnaireSteps.TermsAndConditions);

type UseAmlQuestionnaireProps = {
	isUniqueRequirement?: boolean;
	requirement: UserRequirement;
};

export const useAmlQuestionnaire = ({ isUniqueRequirement, requirement }: UseAmlQuestionnaireProps) => {
	const {
		isLoading: isLoadingRequirement,
		isSuccess: isSuccessRequirement,
		mutate: mutateRequirement
	} = useSubmitRequirementStateByIdentifierQuery();

	const [stepIndex, setStepIndex] = useAtom<QuestionnaireSteps>(stepIndexAtom);
	const [state, dispatch] = useAtom(amlQuestionnaireReducerAtom);
	const setShowRequirementPage = useSetAtom(uniqueRequirementAtom);

	const onSuccess = () => {
		if (isUniqueRequirement) setShowRequirementPage(false);
	};

	useEffect(() => {
		return () => dispatch({ type: 'RESET' });
	}, []);

	const handleSubmit = () => {
		const payload = { ...state.formData };

		const birthDate = dateFormats.format(payload.birthDate, StringDateApiFormatType);

		payload.birthDate = birthDate;

		if (!payload.addressMunicipality?.trim()) payload.addressMunicipality = undefined;
		if (!payload.region?.trim()) payload.region = undefined;
		if (!payload.sourceOfIncomeOther?.trim()) payload.sourceOfIncomeOther = undefined;
		if (!payload.professionOther?.trim()) payload.professionOther = undefined;

		mutateRequirement(
			{
				requirementData: payload as UserAMLQuestionnaireUserProvidedValues,
				requirementIdentifier: requirement.identifier
			},
			{ onSuccess }
		);
	};

	const isFirstButtonDisabled = (() => {
		const stepKey = QuestionnaireSteps[stepIndex] as keyof typeof QuestionnaireSteps;

		return !state.stepValidity[stepKey];
	})();

	return {
		handleSubmit,
		isFirstButtonDisabled,
		isSubmitLoading: isLoadingRequirement,
		isSuccess: isSuccessRequirement,
		setStepIndex
	};
};
